<template>
  <transition name="modal-fade">
    <div v-if="isOpen" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>
            <span class="notification-icon">
              <IconSvg name="bell-solid-full" size="md" />
            </span>
            通知
          </h2>
          <button class="close-button" @click="closeModal">×</button>
        </div>

        <!-- タブ -->
        <div class="modal-tabs">
          <button
            class="modal-tab"
            :class="{ active: activeTab === 'system' }"
            @click="activeTab = 'system'"
          >
            <IconSvg name="bell-solid-full" size="sm" />
            お知らせ
            <span v-if="userNotifStore.unreadCount > 0" class="tab-badge">
              {{ userNotifStore.unreadCount }}
            </span>
          </button>
          <button
            class="modal-tab"
            :class="{ active: activeTab === 'deadline' }"
            @click="activeTab = 'deadline'"
          >
            <IconSvg name="calendar-solid-full" size="sm" />
            期限通知
            <span v-if="deadlineCount > 0" class="tab-badge">
              {{ deadlineCount }}
            </span>
          </button>
        </div>

        <div class="modal-body">
          <!-- ローディング -->
          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>読み込み中...</p>
          </div>

          <!-- ===== お知らせタブ ===== -->
          <div v-else-if="activeTab === 'system'">
            <div
              v-if="userNotifStore.notifications.length === 0"
              class="empty-state"
            >
              <div class="empty-icon">
                <IconSvg name="bell-solid-full" size="xl" />
              </div>
              <p>お知らせはありません</p>
            </div>
            <div v-else>
              <div class="system-notif-toolbar">
                <button
                  v-if="userNotifStore.unreadCount > 0"
                  class="btn-read-all"
                  @click="userNotifStore.markAllAsRead()"
                >
                  すべて既読にする
                </button>
              </div>
              <div
                v-for="n in userNotifStore.notifications"
                :key="n.NOTIFICATION_NO"
                class="system-notif-item"
                :class="{ unread: n.IS_READ_FLG === '0' }"
                @click="userNotifStore.markAsRead(n.NOTIFICATION_NO)"
              >
                <div class="system-notif-header">
                  <span
                    class="system-notif-type"
                    :class="`type-${n.NOTIFICATION_TYPE?.toLowerCase()}`"
                  >
                    {{ typeLabel(n.NOTIFICATION_TYPE) }}
                  </span>
                  <span class="system-notif-date">{{
                    formatDate(n.ENTRY_YMD)
                  }}</span>
                  <span v-if="n.IS_READ_FLG === '0'" class="unread-dot"></span>
                </div>
                <div class="system-notif-title">{{ n.TITLE }}</div>
                <div class="system-notif-body">{{ n.BODY_TEXT }}</div>
              </div>
            </div>
          </div>

          <!-- ===== 期限通知タブ ===== -->
          <div v-else-if="activeTab === 'deadline'">
            <!-- 通知なし -->
            <div v-if="!hasNotifications" class="empty-state">
              <div class="empty-icon">
                <IconSvg name="check-solid-full" size="xl" />
              </div>
              <p>期限が近い通知はありません</p>
            </div>

            <!-- 通知リスト -->
            <div v-else class="notification-content">
              <!-- 期限切れタスク -->
              <div
                v-if="overdueNotifications.length > 0"
                class="notification-group overdue-group"
              >
                <div class="group-header">
                  <span class="group-icon">
                    <IconSvg name="triangle-exclamation-solid-full" size="md" />
                  </span>
                  <h3 class="group-title">
                    期限切れ ({{ overdueNotifications.length }})
                  </h3>
                </div>
                <div class="notification-list">
                  <div
                    v-for="item in overdueNotifications"
                    :key="`${item.shioriId}-${item.CHECKLIST_NO}`"
                    class="notification-item overdue-item"
                    @click="goToChecklist(item)"
                  >
                    <div
                      class="item-priority"
                      :class="`priority-${
                        item.PRIORITY_LEVEL?.toLowerCase() || 'normal'
                      }`"
                    >
                      {{ getPriorityEmoji(item.PRIORITY_LEVEL) }}
                    </div>
                    <div class="item-content">
                      <div class="item-header">
                        <strong class="item-name">{{ item.ITEM_NAME }}</strong>
                        <span class="item-shiori">{{
                          getShioriName(item.shioriId)
                        }}</span>
                      </div>
                      <div class="item-meta">
                        <span class="item-deadline">{{
                          formatDeadlineLabel(item.DEADLINE)
                        }}</span>
                        <span
                          v-if="item.ASSIGNED_MEMBER_NAME"
                          class="item-assignee"
                        >
                          <IconSvg name="user-solid-full" size="xs" />
                          {{ item.ASSIGNED_MEMBER_NAME }}
                        </span>
                        <span class="item-category">{{
                          item.CATEGORY_NAME || "その他"
                        }}</span>
                      </div>
                    </div>
                    <button class="item-arrow">→</button>
                  </div>
                </div>
              </div>

              <!-- 期限が近いタスク -->
              <div
                v-if="upcomingNotifications.length > 0"
                class="notification-group upcoming-group"
              >
                <div class="group-header">
                  <span class="group-icon">
                    <IconSvg name="clock-solid-full" size="md" />
                  </span>
                  <h3 class="group-title">
                    期限が近い ({{ upcomingNotifications.length }})
                  </h3>
                </div>
                <div class="notification-list">
                  <div
                    v-for="item in upcomingNotifications"
                    :key="`${item.shioriId}-${item.CHECKLIST_NO}`"
                    class="notification-item upcoming-item"
                    @click="goToChecklist(item)"
                  >
                    <div
                      class="item-priority"
                      :class="`priority-${
                        item.PRIORITY_LEVEL?.toLowerCase() || 'normal'
                      }`"
                    >
                      {{ getPriorityEmoji(item.PRIORITY_LEVEL) }}
                    </div>
                    <div class="item-content">
                      <div class="item-header">
                        <strong class="item-name">{{ item.ITEM_NAME }}</strong>
                        <span class="item-shiori">{{
                          getShioriName(item.shioriId)
                        }}</span>
                      </div>
                      <div class="item-meta">
                        <span class="item-deadline">{{
                          formatDeadlineLabel(item.DEADLINE)
                        }}</span>
                        <span
                          v-if="item.ASSIGNED_MEMBER_NAME"
                          class="item-assignee"
                        >
                          <IconSvg name="user-solid-full" size="xs" />
                          {{ item.ASSIGNED_MEMBER_NAME }}
                        </span>
                        <span class="item-category">{{
                          item.CATEGORY_NAME || "その他"
                        }}</span>
                      </div>
                    </div>
                    <button class="item-arrow">→</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import IconSvg from "@/components/IconSvg.vue";
import { useUserNotificationStore } from "@/store/user-notification-store";

export default {
  name: "NotificationModal",
  components: {
    IconSvg,
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const router = useRouter();
    const userNotifStore = useUserNotificationStore();
    const loading = ref(false);
    const activeTab = ref("system");

    const isOpen = computed({
      get: () => props.modelValue,
      set: (value) => emit("update:modelValue", value),
    });

    const closeModal = () => {
      isOpen.value = false;
    };

    // チェックリスト期限通知
    const allNotifications = computed(() =>
      userNotifStore.deadlineNotifications.map((item) => ({
        ...item,
        shioriId: item.SHIORI_NOV,
      })),
    );

    const overdueNotifications = computed(() => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return allNotifications.value.filter((item) => {
        if (!item.DEADLINE) return false;
        const deadline = new Date(item.DEADLINE);
        deadline.setHours(0, 0, 0, 0);
        return deadline < today;
      });
    });

    const upcomingNotifications = computed(() => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const threeDaysLater = new Date(today);
      threeDaysLater.setDate(threeDaysLater.getDate() + 3);
      return allNotifications.value.filter((item) => {
        if (!item.DEADLINE) return false;
        const deadline = new Date(item.DEADLINE);
        deadline.setHours(0, 0, 0, 0);
        return deadline >= today && deadline <= threeDaysLater;
      });
    });

    const hasNotifications = computed(
      () =>
        overdueNotifications.value.length > 0 ||
        upcomingNotifications.value.length > 0,
    );

    const deadlineCount = computed(
      () =>
        overdueNotifications.value.length + upcomingNotifications.value.length,
    );

    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(
        2,
        "0",
      )}/${String(date.getDate()).padStart(2, "0")}`;
    };

    const getShioriName = (shioriId) => {
      const item = allNotifications.value.find(
        (notification) => notification.SHIORI_NOV === shioriId,
      );
      return item?.SHIORI_NAME || "不明なしおり";
    };

    const goToChecklist = (item) => {
      const shioriId = item?.shioriId || item?.SHIORI_NOV;
      if (!shioriId) return;

      const tab = item?.ITEM_TYPE === "BELONGINGS" ? "belongings" : "todo";
      router.push({
        path: `/shiori/${shioriId}`,
        query: { tab },
      });
      closeModal();
    };

    const formatDeadlineLabel = (dateString) => {
      if (!dateString) return "期限: 未設定";

      const deadline = new Date(dateString);
      if (Number.isNaN(deadline.getTime())) {
        return `期限: ${dateString}`;
      }

      deadline.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diffDays = Math.round(
        (deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
      );

      const formatted = formatDate(dateString);
      if (diffDays < 0) {
        return `期限: ${formatted}（${Math.abs(diffDays)}日超過）`;
      }
      if (diffDays === 0) {
        return `期限: ${formatted}（本日）`;
      }
      if (diffDays === 1) {
        return `期限: ${formatted}（明日）`;
      }
      return `期限: ${formatted}（あと${diffDays}日）`;
    };

    const getPriorityEmoji = () => "";

    const typeLabel = (type) => {
      const map = { INFO: "お知らせ", WARNING: "警告", SYSTEM: "システム" };
      return map[type] || type;
    };

    // モーダルが開かれた時にデータ取得
    watch(isOpen, async (newValue) => {
      if (newValue) {
        loading.value = true;
        try {
          await Promise.all([
            userNotifStore.fetchMyNotifications(),
            userNotifStore.fetchDeadlineNotifications(),
          ]);
        } catch (error) {
          console.error("通知データ取得エラー:", error);
        } finally {
          loading.value = false;
        }
      }
    });

    return {
      isOpen,
      loading,
      activeTab,
      closeModal,
      userNotifStore,
      hasNotifications,
      overdueNotifications,
      upcomingNotifications,
      deadlineCount,
      formatDate,
      formatDeadlineLabel,
      getShioriName,
      goToChecklist,
      getPriorityEmoji,
      typeLabel,
    };
  },
};
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

/* タブ */
.modal-tabs {
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  padding: 0 1.5rem;
  gap: 0;
  flex-shrink: 0;
}

.modal-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 12px 20px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  font-size: 0.9rem;
  color: #6b7280;
  cursor: pointer;
  font-weight: 500;
  margin-bottom: -2px;
  transition: color 0.2s, border-color 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.modal-tab.active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
  font-weight: 600;
}

.tab-badge {
  background: #ef4444;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 9999px;
  min-width: 18px;
  text-align: center;
}

/* システム通知 */
.system-notif-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.btn-read-all {
  background: none;
  border: 1px solid #d1d5db;
  color: #6b7280;
  font-size: 0.8rem;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-read-all:hover {
  background: #f3f4f6;
}

.system-notif-item {
  padding: 14px 16px;
  border-radius: 10px;
  margin-bottom: 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: background 0.2s;
}

.system-notif-item.unread {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.system-notif-item:hover {
  background: #f0f0f0;
}

.system-notif-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.system-notif-type {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 9999px;
}

.type-info {
  background: #dbeafe;
  color: #1d4ed8;
}
.type-warning {
  background: #fef3c7;
  color: #92400e;
}
.type-system {
  background: #ede9fe;
  color: #6d28d9;
}

.system-notif-date {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-left: auto;
}

.unread-dot {
  width: 8px;
  height: 8px;
  background: #3b82f6;
  border-radius: 50%;
  flex-shrink: 0;
}

.system-notif-title {
  font-weight: 700;
  font-size: 0.9rem;
  color: #1f2937;
  margin-bottom: 4px;
}

.system-notif-body {
  font-size: 0.85rem;
  color: #4b5563;
  line-height: 1.5;
  white-space: pre-wrap;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 2px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.notification-icon {
  font-size: 1.75rem;
  animation: ring 2s ease-in-out infinite;
}

@keyframes ring {
  0%,
  100% {
    transform: rotate(0deg);
  }
  10%,
  30% {
    transform: rotate(-15deg);
  }
  20%,
  40% {
    transform: rotate(15deg);
  }
}

.close-button {
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: #f3f4f6;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

/* ローディング */
.loading-state {
  text-align: center;
  padding: 3rem 2rem;
  color: #6b7280;
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 1rem;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #b4cf9e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 空の状態 */
.empty-state {
  text-align: center;
  padding: 3rem 2rem;
  color: #6b7280;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

/* 通知コンテンツ */
.notification-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.notification-group {
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid #e5e7eb;
}

.overdue-group {
  border-color: #fecaca;
  background: #fef2f2;
}

.upcoming-group {
  border-color: #fed7aa;
  background: #fff7ed;
}

.group-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: white;
  border-bottom: 1px solid #e5e7eb;
}

.group-icon {
  font-size: 1.25rem;
}

.group-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
}

.notification-list {
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.notification-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 8px;
  background: white;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s;
}

.notification-item:hover {
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: #b4cf9e;
}

.item-priority {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.item-content {
  flex: 1;
  min-width: 0;
}

.item-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.item-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
}

.item-shiori {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  background: #f3f4f6;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: #6b7280;
}

.item-deadline {
  font-weight: 500;
}

.overdue-item .item-deadline {
  color: #dc2626;
  font-weight: 600;
}

.upcoming-item .item-deadline {
  color: #d97706;
  font-weight: 600;
}

.item-assignee {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.item-category {
  background: #e5e7eb;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
}

.item-arrow {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #9ca3af;
  cursor: pointer;
  transition: color 0.2s, transform 0.2s;
  flex-shrink: 0;
}

.notification-item:hover .item-arrow {
  color: #b4cf9e;
  transform: translateX(4px);
}

/* レスポンシブ */
@media (max-width: 640px) {
  .modal-content {
    max-height: calc(100dvh - 1rem);
  }

  .modal-header h2 {
    font-size: 1.25rem;
  }

  .notification-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .item-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .item-arrow {
    align-self: flex-end;
  }
}
</style>
